<template>
  <div class="ms-bulk-actions-checkpoints-table">
    <div class="ms-bulk-actions-checkpoints-table__toolbar">
      <div class="ms-bulk-actions-checkpoints-table__toolbar-left">
        <SearchByModal
          :label="localChipText.length > 0 ? 'More filters' : 'Filter'"
          ghostText="Search Checkpoint"
          type="button"
          :length="localChipText.length"
          :chipText="localChipText"
          :valueSearch="searchText"
          :counterSection="true"
          :levelCounter="effectiveLevelCounter"
          :removeCounterLevelFilter="removeCounterLevelFilter"
          :filterLevels="selectedFilterLevels"
          :filterGroups="filterGroups"
          :filterType="filterType"
          :statusOptions="effectiveStatusOptions"
          :groupsItems="groupsItems"
          :defaultSelected="localChipText"
          :add="false"
          :itemsSelectedNumber="isMultiselectMode ? selectedIds.length : 0"
          @onSearch="handleSearch"
          @removeFilter="handleRemoveFilter"
          @filterTypes="handleFilterTypes"
          @clearSelection="handleClearSelection"
          @onCheckedItemsChanged="handleCheckedItemsChanged"
        />
      </div>
      <div class="ms-bulk-actions-checkpoints-table__toolbar-right">
        <EditCheckpointsTableBulkActionsDropDown
          v-model="bulkAction"
          :disabled="!isMultiselectMode || selectedIds.length === 0"
          @select="handleBulkAction"
          :selectedItemsCounter="selectedIds.length"
        />
        <MultiselectTableSwitch
          class="ms-bulk-actions-checkpoints-table__switch"
          :value="!isGridView"
          @input="$emit('multiselectTableSwitch', $event)"
        />
      </div>
    </div>

    <CheckpointBulkActivateDialog
      :show="showActivateDialog"
      @onActivate="handleActivateCheckpoints"
      @onClose="showActivateDialog = false"
    />

    <CheckpointBulkDeleteDialog
      :show="showDeleteDialog"
      @onDelete="handleDeleteCheckpoints"
      @onClose="showDeleteDialog = false"
    />

    <CheckpointsBulkArchiveDialog
      :show="showArchiveDialog"
      @onArchive="handleArchiveCheckpoints"
      @onClose="showArchiveDialog = false"
    />

    <CheckpointBulkRetireDialog
      :show="showRetireDialog"
      @onRetire="handleRetireCheckpoints"
      @onClose="showRetireDialog = false"
    />

    <CheckpointsBulkMoveDialog
      :dialog="showMoveDialog"
      objectType="Responsibility"
      buttonLabel="Move in Bulk"
      :items="bulkMoveResponsibilitiesItems"
      :existingNamesList="bulkMoveExistingNamesList"
      :showError="bulkMoveError"
      :hasMoreData="hasMoreBulkMoveData"
      :isLoadingMore="isLoadingMoreBulkMove"
      @dialogItem="handleBulkMoveConfirm"
      @back="closeBulkMoveDialog"
      @item-selected="handleBulkMoveResponsibilitySelected"
      @add-item-selected="handleBulkMoveResponsibilitySelected"
      @load-more="loadMoreBulkMoveResponsibilities"
      @search="searchBulkMoveResponsibilities"
    />

    <!-- List View (Table) -->
    <div v-if="!isGridView" class="ms-bulk-actions-checkpoints-table__table-wrap">
      <table class="ms-bulk-actions-checkpoints-table__table">
        <thead>
          <tr class="ms-bulk-actions-checkpoints-table__head-row">
            <th
              v-if="isMultiselectMode"
              class="ms-bulk-actions-checkpoints-table__th ms-bulk-actions-checkpoints-table__th--select"
            >
              <input
                class="ms-bulk-actions-checkpoints-table__checkbox"
                type="checkbox"
                :checked="headerCheckboxSelected"
                aria-label="Criteria"
                @change="toggleHeaderCheckbox"
              />
            </th>
            <th class="ms-bulk-actions-checkpoints-table__th">Criteria</th>
            <th class="ms-bulk-actions-checkpoints-table__th ms-bulk-actions-checkpoints-table__th--status">
              Status
            </th>
            <th class="ms-bulk-actions-checkpoints-table__th ms-bulk-actions-checkpoints-table__th--actions" />
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="item in displayedItems"
            :key="item.id"
            class="ms-bulk-actions-checkpoints-table__row"
            :class="{
              'ms-bulk-actions-checkpoints-table__row--compact': isMultiselectMode,
            }"
            @click="handleRowClick(item)"
          >
            <td
              v-if="isMultiselectMode"
              class="ms-bulk-actions-checkpoints-table__td ms-bulk-actions-checkpoints-table__td--select"
              @click.stop
            >
              <input
                class="ms-bulk-actions-checkpoints-table__checkbox"
                type="checkbox"
                :checked="selectedSet.has(item.id)"
                @change="toggleOne(item.id)"
                :aria-label="`Select checkpoint ${item.title}`"
              />
            </td>
            <td class="ms-bulk-actions-checkpoints-table__td">
              <SelectableCheckpointLine
                :title="item.title"
                :level="item.level"
                :selected="isMultiselectMode ? selectedSet.has(item.id) : true"
              />
            </td>
            <td class="ms-bulk-actions-checkpoints-table__td ms-bulk-actions-checkpoints-table__td--status">
              <span class="ms-bulk-actions-checkpoints-table__status-pill" :class="statusPillClass(item.status)">
                {{ item.status }}
              </span>
            </td>
            <td class="ms-bulk-actions-checkpoints-table__td ms-bulk-actions-checkpoints-table__td--actions" @click.stop>
              <div
                class="ms-bulk-actions-checkpoints-table__chatter"
                role="button"
                tabindex="0"
                aria-label="Open chatter"
                @click="$emit('openChatter', item)"
                @keydown.enter.prevent="$emit('openChatter', item)"
                @keydown.space.prevent="$emit('openChatter', item)"
              >
                <ChatterIcon :unread="Number(item.chatterCount) || 0" />
              </div>
            </td>
          </tr>

          <tr v-if="filteredItems.length === 0" class="ms-bulk-actions-checkpoints-table__empty-row">
            <td class="ms-bulk-actions-checkpoints-table__td ms-bulk-actions-checkpoints-table__empty" colspan="4">
              No checkpoints found
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Grid View (Cards) -->
    <div v-else class="ms-bulk-actions-checkpoints-table__grid-wrap">
      <div v-if="isMultiselectMode" class="ms-bulk-actions-checkpoints-table__grid-header">
        <label class="ms-bulk-actions-checkpoints-table__select-all-label">
          <input
            class="ms-bulk-actions-checkpoints-table__checkbox"
            type="checkbox"
            :checked="headerCheckboxSelected"
            aria-label="Select All"
            @change="toggleHeaderCheckbox"
          />
          <span class="ms-bulk-actions-checkpoints-table__select-all-text">Select All</span>
        </label>
      </div>

      <div v-if="filteredItems.length === 0" class="ms-bulk-actions-checkpoints-table__empty-grid">
        No checkpoints found
      </div>

      <div v-else class="ms-bulk-actions-checkpoints-table__grid">
        <div
          v-for="item in displayedItems"
          :key="item.id"
          class="ms-bulk-actions-checkpoints-table__card"
          :class="{
            'ms-bulk-actions-checkpoints-table__card--selected': isMultiselectMode && selectedSet.has(item.id),
          }"
          @click="handleRowClick(item)"
        >
          <div v-if="isMultiselectMode" class="ms-bulk-actions-checkpoints-table__card-checkbox-wrapper" @click.stop>
            <input
              class="ms-bulk-actions-checkpoints-table__checkbox"
              type="checkbox"
              :checked="selectedSet.has(item.id)"
              @change="toggleOne(item.id)"
              :aria-label="`Select checkpoint ${item.title}`"
            />
          </div>

          <div class="ms-bulk-actions-checkpoints-table__card-content">
            <div class="ms-bulk-actions-checkpoints-table__card-header">
              <SelectableCheckpointLine
                :title="item.title"
                :level="item.level"
                :selected="isMultiselectMode ? selectedSet.has(item.id) : true"
              />
            </div>

            <div class="ms-bulk-actions-checkpoints-table__card-footer">
              <span class="ms-bulk-actions-checkpoints-table__status-pill" :class="statusPillClass(item.status)">
                {{ item.status }}
              </span>
              <div
                class="ms-bulk-actions-checkpoints-table__chatter ms-bulk-actions-checkpoints-table__chatter--card"
                role="button"
                tabindex="0"
                aria-label="Open chatter"
                @click.stop="$emit('openChatter', item)"
                @keydown.enter.prevent="$emit('openChatter', item)"
                @keydown.space.prevent="$emit('openChatter', item)"
              >
                <ChatterIcon :unread="Number(item.chatterCount) || 0" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, getCurrentInstance } from 'vue';
import SearchByModal from '@components/SearchBar/SearchByModal.vue';
import EditCheckpointsTableBulkActionsDropDown from '@components/EditCheckpointsTableBulkActionsDropDown/EditCheckpointsTableBulkActionsDropDown.vue';
import MultiselectTableSwitch from '@components/MultiselectTableSwitch/MultiselectTableSwitch.vue';
import SelectableCheckpointLine from '@components/SelectableCheckpointLine/SelectableCheckpointLine.vue';
import ChatterIcon from '@components/ChatterIcon/ChatterIcon.vue';
import CheckpointBulkActivateDialog from '@components/CheckpointBulkActivateDialog/CheckpointBulkActivateDialog.vue';
import CheckpointBulkDeleteDialog from '@components/CheckpointBulkDeleteDialog/CheckpointBulkDeleteDialog.vue';
import CheckpointsBulkArchiveDialog from '@components/CheckpointsBulkArchiveDialog/CheckpointsBulkArchiveDialog.vue';
import CheckpointBulkRetireDialog from '@components/CheckpointBulkRetireDialog/CheckpointBulkRetireDialog.vue';
import CheckpointsBulkMoveDialog from '@components/CheckpointsBulkMoveDialog/CheckpointsBulkMoveDialog.vue';
import {
  bulkActivateCheckpoints,
  bulkDeleteCheckpoints,
  bulkArchiveCheckpoints,
  bulkRetireCheckpoints,
  bulkMoveCheckpoints,
  getListofResponsibilityV2,
} from '@/api';
import { useNotificationStore } from '@/store/pinia/notifications';
import { useSelfAppointPromptStore } from '@/store/pinia/selfAppointPrompt';
import store from '../../account/src/store';
import { filterCheckpointIdsForBulkActivate } from '@/utils/draftCheckpointValidity';

import { isLevelSelected, normalizeBadgeLevel } from "@components/utils/normalizeBadgeLevel";
const instance = getCurrentInstance();
const { proxy } = instance;
const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
  appliedFilters: {
    type: Array,
    default: () => ['Active'],
  },
  filterGroups: {
    type: Array,
    default: () => [],
  },
  filterType: {
    type: String,
    default: 'Checkpoint',
  },
  statusOptions: {
    type: Array,
    default: () => ['draft', 'active', 'retired', 'inactive'],
  },
  groupsItems: {
    type: Array,
    default: () => [],
  },
  valueSearch: {
    type: String,
    default: '',
  },
  multiselectDefault: {
    type: Boolean,
    default: null,
  },
  compactDefault: {
    type: Boolean,
    default: false,
  },
  isGridView: {
    type: Boolean,
    default: false,
  },
  accountId: {
    type: [Number, String],
    required: true,
  },
  responsibilityId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits([
  'update:modelValue',
  'search',
  'filterTypes',
  'removeFilter',
  'clearSelection',
  'onCheckedItemsChanged',
  'bulkAction',
  'openChatter',
  'rowClick',
  'multiselectTableSwitch',
  'refreshCheckpoints',
  'switchToDefaultView',
]);

const searchText = ref(props.valueSearch);
watch(
  () => props.valueSearch,
  (next) => {
    searchText.value = next ?? '';
  }
);

const bulkAction = ref(null);
const showActivateDialog = ref(false);
const showDeleteDialog = ref(false);
const showArchiveDialog = ref(false);
const showRetireDialog = ref(false);
const showMoveDialog = ref(false);
const bulkMoveResponsibilitiesItems = ref([]);
const bulkMoveExistingNamesList = ref([]);
const selectedTargetResponsibilityId = ref(null);
const bulkMoveError = ref('');
const bulkMovePage = ref(1);
const bulkMoveSearchTerm = ref('');
const hasMoreBulkMoveData = ref(false);
const isLoadingMoreBulkMove = ref(false);
const isMultiselectMode = ref(
  props.multiselectDefault === null ? Boolean(props.compactDefault) : Boolean(props.multiselectDefault)
);
const headerCheckboxSelected = ref(false);

const localChipText = ref(Array.isArray(props.appliedFilters) ? props.appliedFilters : []);
watch(
  () => props.appliedFilters,
  (next) => {
    localChipText.value = Array.isArray(next) ? next : [];
  },
  { deep: true }
);

const selectedFilterLevels = ref([]);
const removeCounterLevelFilter = ref(false);

const localPagination = ref({
  page: 1,
  perPage: 10,
  total: 0,
});

const selectedIds = computed(() => (Array.isArray(props.modelValue) ? props.modelValue : []));
const selectedSet = computed(() => new Set(selectedIds.value));

const selectedIdsInFiltered = computed(() => {
  const filteredIds = new Set((filteredItems.value ?? []).map((r) => r?.id).filter((id) => id != null));
  return (selectedIds.value ?? []).filter((id) => filteredIds.has(id));
});

const allSelected = computed(() => {
  const total = filteredItems.value.length;
  if (total === 0) return false;
  return selectedIdsInFiltered.value.length === total;
});

const someSelected = computed(() => {
  const total = filteredItems.value.length;
  const selected = selectedIdsInFiltered.value.length;
  return selected > 0 && selected < total;
});

function toggleAll(checked) {
  if (!checked) {
    emit('update:modelValue', []);
    return;
  }
  emit(
    'update:modelValue',
    filteredItems.value.map((i) => i.id)
  );
}

function toggleOne(id) {
  const next = new Set(selectedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  emit('update:modelValue', Array.from(next));
  // Uncheck header checkbox when individual items are toggled
  headerCheckboxSelected.value = false;
}

function toggleHeaderCheckbox() {
  headerCheckboxSelected.value = !headerCheckboxSelected.value;
  if (headerCheckboxSelected.value) {
    // Select all filtered items
    emit(
      'update:modelValue',
      filteredItems.value.map((i) => i.id)
    );
  } else {
    // Deselect all
    emit('update:modelValue', []);
  }
}

function clearSelection() {
  emit('update:modelValue', []);
  headerCheckboxSelected.value = false;
}

function handleSearch(text) {
  searchText.value = text ?? '';
  emit('search', searchText.value);
}

const effectiveStatusOptions = computed(() => {
  if (Array.isArray(props.statusOptions) && props.statusOptions.length > 0) return props.statusOptions;

  const uniq = new Set();
  for (const row of props.items ?? []) {
    const s = String(row?.status ?? '').trim();
    if (s) uniq.add(s);
  }
  return Array.from(uniq);
});

function normalizeStatus(value) {
  const s = String(value ?? '').trim().toLowerCase();
  if (!s) return '';
  if (s === 'retire') return 'retired';
  if (s === 'inactive') return 'retired'; // treat "Inactive" as same bucket as "Retired" in UI
  return s;
}

const baseFilteredItems = computed(() => {
  const q = (searchText.value || '').trim().toLowerCase();
  const statusOptionsLower = new Set(
    (effectiveStatusOptions.value ?? []).map((s) => normalizeStatus(String(s)))
  );
  const chips = (localChipText.value ?? []).filter(Boolean).map((c) => String(c));

  const statusChips = chips
    .map((c) => (c === 'Show' ? 'Show Archived' : c))
    .map((c) => normalizeStatus(c))
    .filter((c) => statusOptionsLower.has(c));

  const selectedStatuses = new Set(statusChips);

  return (props.items ?? []).filter((i) => {
    if (q) {
      const hay = String(i?.title ?? '').toLowerCase();
      if (!hay.includes(q)) return false;
    }

    // OR across selected statuses (checkbox multi-select)
    if (selectedStatuses.size > 0) {
      const status = normalizeStatus(i?.status);
      if (!selectedStatuses.has(status)) return false;
    }

    return true;
  });
});

const filteredItems = computed(() => {
  const selectedLevels = (selectedFilterLevels.value ?? [])
    .filter(Boolean)
    .map((s) => String(s).trim().toLowerCase());

  if (selectedLevels.length === 0) return baseFilteredItems.value;

  return baseFilteredItems.value.filter((row) => {
    const level = String(row?.level ?? '').toLowerCase();
    return (
      isLevelSelected(selectedFilterLevels.value, row?.level) ||
      selectedLevels.some((lvl) => level.includes(lvl))
    );
  });
});

const displayedItems = computed(() => filteredItems.value);

function selectDefaultFromVisible() {
  const first = displayedItems.value[0] ?? filteredItems.value[0];
  if (!first) return;
  if (selectedIds.value.length > 0) return;
  emit('update:modelValue', [first.id]);
}

watch(
  isMultiselectMode,
  (next, prev) => {
    if (prev === undefined) return;
    if (next) {
      selectDefaultFromVisible();
    } else {
      clearSelection();
    }
  }
);

// If the component starts in multiselect mode, apply the default selection.
if (isMultiselectMode.value) {
  selectDefaultFromVisible();
}

function handleRowClick(item) {
  if (isMultiselectMode.value) {
    toggleOne(item.id);
    return;
  }
  emit('rowClick', item);
}

const levelCounter = computed(() => {
  const counters = {
    Apprentice: 0,
    Proficient: 0,
    Coach: 0,
    Architect: 0,
  };
  for (const item of props.items) {
    const lvl = normalizeBadgeLevel(item.level);
    if (lvl === 'apprentice') counters.Apprentice += 1;
    else if (lvl === 'proficient') counters.Proficient += 1;
    else if (lvl === 'coach') counters.Coach += 1;
    else if (lvl === 'architect') counters.Architect += 1;
  }
  return counters;
});

const effectiveLevelCounter = computed(() => {
  const next = {
    Apprentice: 0,
    Proficient: 0,
    Coach: 0,
    Architect: 0,
  };

  const rows = baseFilteredItems.value ?? [];
  for (const row of rows) {
    const lvl = String(row?.level ?? '').toLowerCase();
    if (lvl.includes('apprentice')) next.Apprentice += 1;
    else if (lvl.includes('professional') || lvl.includes('proficient')) next.Proficient += 1;
    else if (lvl.includes('coach')) next.Coach += 1;
    else if (lvl.includes('master') || lvl.includes('architect')) next.Architect += 1;
  }
  return next;
});

function handleFilterTypes(levels) {
  selectedFilterLevels.value = Array.isArray(levels) ? levels : [];
  emit('filterTypes', selectedFilterLevels.value);
}

function handleRemoveFilter(removedFilter) {
  const removed = String(removedFilter ?? '').trim();
  if (removed) {
    localChipText.value = (localChipText.value ?? []).filter((c) => c !== removed);
  }
  emit('removeFilter', removedFilter);
}

function handleClearSelection(event) {
  // Clear chips + levels + search
  if (event) {
    const removed = String(event ?? '').trim();
    if (removed) {
      localChipText.value = (localChipText.value ?? []).filter((c) => c !== removed);
    }
  } else {
    localChipText.value = [];
    selectedFilterLevels.value = [];
    searchText.value = '';
    emit('search', '');
  }

  removeCounterLevelFilter.value = true;
  setTimeout(() => {
    removeCounterLevelFilter.value = false;
  }, 0);

  emit('clearSelection', event);
}

function handleCheckedItemsChanged(event) {
  const mapped = Array.isArray(event)
    ? event
        .map((item) => {
          if (item == null) return null;
          if (typeof item === 'string') return item;
          if (typeof item === 'object' && Object.prototype.hasOwnProperty.call(item, 'value')) {
            return item.value;
          }
          return null;
        })
        .filter(Boolean)
    : [];
  localChipText.value = mapped;
  emit('onCheckedItemsChanged', event);
}

function handlePaginationRequest(event) {
  const nextPage = Number(event?.page ?? 1);
  if (!Number.isFinite(nextPage) || nextPage < 1) return;
  localPagination.value = { ...localPagination.value, page: nextPage };
}

watch(
  () => [searchText.value, selectedFilterLevels.value, localChipText.value],
  () => {
    // Snap back to page 1 whenever filters change
    localPagination.value = { ...localPagination.value, page: 1 };
  },
  { deep: true }
);

watch(
  () => props.isGridView,
  () => {
    // Reset to page 1 when switching between grid and list views
    localPagination.value = { ...localPagination.value, page: 1 };
  }
);

watch(
  () => filteredItems.value.length,
  (len) => {
    const perPage = Number(localPagination.value.perPage ?? 10);
    const totalPages = Math.max(1, Math.ceil((len ?? 0) / perPage));
    const current = Number(localPagination.value.page ?? 1);
    if (current > totalPages) {
      localPagination.value = { ...localPagination.value, page: totalPages };
    }
  }
);

watch(
  () => selectedIds.value.length,
  (len) => {
    // If all items are deselected externally, uncheck the header checkbox
    if (len === 0) {
      headerCheckboxSelected.value = false;
    }
  }
);

function statusPillClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return 'ms-bulk-actions-checkpoints-table__status-pill--active';
  if (s === 'draft') return 'ms-bulk-actions-checkpoints-table__status-pill--draft';
  if (s === 'archived') return 'ms-bulk-actions-checkpoints-table__status-pill--archived';
  if (s === 'retired' || s === 'inactive') return 'ms-bulk-actions-checkpoints-table__status-pill--retired';
  return 'ms-bulk-actions-checkpoints-table__status-pill--default';
}

function handleBulkAction(action) {
  if (action === 'activate') {
    bulkAction.value = null;
    showActivateDialog.value = true;
  } else if (action === 'delete') {
    bulkAction.value = null;
    showDeleteDialog.value = true;
  } else if (action === 'archive') {
    bulkAction.value = null;
    showArchiveDialog.value = true;
  } else if (action === 'retire') {
    bulkAction.value = null;
    showRetireDialog.value = true;
  } else if (action === 'move') {
    bulkAction.value = null;
    loadBulkMoveResponsibilities();
  } else {
    emit('bulkAction', action);
  }
}

const BULK_MOVE_PAGE_SIZE = 10;

function mapResponsibilityItem(item) {
  return {
    id: item.id,
    label: item.code_str ? item.code_str.match(/\d+/)?.[0] : item.code,
    code: item.code_str ? item.code_str.match(/\d+/)?.[0] : item.code,
    name: item.name,
    status: item.status,
  };
}

async function fetchBulkMoveResponsibilities(isLoadMore = false) {
  if (!props.accountId) return;
  const currentRespId = props.responsibilityId;
  const limit = BULK_MOVE_PAGE_SIZE;
  const offset = (bulkMovePage.value - 1) * limit;
  const search = bulkMoveSearchTerm.value || '';

  try {
    isLoadingMoreBulkMove.value = true;
    if (!isLoadMore) {
      bulkMoveResponsibilitiesItems.value = [];
    }
    const response = await getListofResponsibilityV2(
      props.accountId,
      limit,
      offset,
      search,
      {},
      'name'
    );
    const results = response?.data?.results ?? [];
    const totalCount = response?.data?.count ?? 0;
    const filtered = results.filter((item) => item.id != currentRespId);
    const mapped = filtered.map(mapResponsibilityItem);

    if (isLoadMore) {
      bulkMoveResponsibilitiesItems.value = [...bulkMoveResponsibilitiesItems.value, ...mapped];
    } else {
      bulkMoveResponsibilitiesItems.value = mapped;
    }
    hasMoreBulkMoveData.value = bulkMovePage.value * limit < totalCount;
  } catch (error) {
    console.error('Error loading responsibilities for bulk move:', error);
    if (!isLoadMore) {
      bulkMoveError.value = 'Failed to load responsibilities.';
    }
  } finally {
    isLoadingMoreBulkMove.value = false;
  }
}

async function loadBulkMoveResponsibilities() {
  if (!props.accountId) return;
  bulkMoveError.value = '';
  bulkMovePage.value = 1;
  bulkMoveSearchTerm.value = '';
  bulkMoveExistingNamesList.value = [];
  selectedTargetResponsibilityId.value = null;
  showMoveDialog.value = true;
  await fetchBulkMoveResponsibilities(false);
}

function loadMoreBulkMoveResponsibilities() {
  if (isLoadingMoreBulkMove.value || !hasMoreBulkMoveData.value) return;
  bulkMovePage.value += 1;
  fetchBulkMoveResponsibilities(true);
}

function searchBulkMoveResponsibilities(searchTerm) {
  bulkMoveSearchTerm.value = searchTerm ?? '';
  bulkMovePage.value = 1;
  fetchBulkMoveResponsibilities(false);
}

function handleBulkMoveResponsibilitySelected(data) {
  if (data && Object.prototype.hasOwnProperty.call(data, 'id')) {
    selectedTargetResponsibilityId.value = data.id;
  } else {
    selectedTargetResponsibilityId.value = null;
  }
}

function closeBulkMoveDialog() {
  showMoveDialog.value = false;
  selectedTargetResponsibilityId.value = null;
  bulkMoveError.value = '';
  bulkMovePage.value = 1;
  bulkMoveSearchTerm.value = '';
}

async function handleBulkMoveConfirm() {
  if (!props.accountId || selectedIds.value.length === 0 || !selectedTargetResponsibilityId.value) {
    return;
  }

  proxy.$loader.show();
  bulkMoveError.value = '';

  try {
    const response = await bulkMoveCheckpoints(
      props.accountId,
      selectedIds.value,
      selectedTargetResponsibilityId.value
    );

    const movedCount = response?.data?.total_moved ?? 0;
    const ignoredCount = response?.data?.total_ignored ?? 0;

    const notificationStore = useNotificationStore();
    notificationStore.success(
      'Checkpoints moved successfully',
      `${movedCount} moved, ${ignoredCount} ignored`
    );

    emit('refreshCheckpoints');
    emit('update:modelValue', []);
    emit('switchToDefaultView');
    closeBulkMoveDialog();
  } catch (error) {
    console.error('Error bulk moving checkpoints:', error);
    bulkMoveError.value = error?.response?.data?.message || 'Failed to move checkpoints.';
  } finally {
    proxy.$loader.hide();
  }
}

const ideaMDP452 = computed(() => Boolean(store.getters['featureFlags/ideaMDP452']));

async function handleActivateCheckpoints() {
  if (!props.accountId || !props.responsibilityId || selectedIds.value.length === 0) {
    return;
  }

  proxy.$loader.setAppLoading(true);

  try {
    const checkpointIds = ideaMDP452.value
      ? filterCheckpointIdsForBulkActivate(props.items, selectedIds.value)
      : selectedIds.value;

    if (checkpointIds.length === 0) {
      showActivateDialog.value = false;
      return;
    }
    
    // Make API call
    const response = await bulkActivateCheckpoints(
      props.accountId,
      props.responsibilityId,
      checkpointIds
    );

    // Extract counts from response
    const activatedCount = response?.data?.activated_count || 0;
    const ignoredCount = response?.data?.ignored_count || 0;

    // Show ConfirmationToast with TIER 4
    const notificationStore = useNotificationStore();
    notificationStore.success(
      'Checkpoints activated successfully',
      `${activatedCount} activated, ${ignoredCount} ignored`
    );

    // Refresh checkpoints (this will be handled asynchronously by parent)
    emit('refreshCheckpoints');

    // Clear selection
    emit('update:modelValue', []);

    // Switch to EditCheckpointsTable default view
    emit('switchToDefaultView');

    // Close dialog after everything completes
    showActivateDialog.value = false;

    if (activatedCount > 0) {
      useSelfAppointPromptStore().requestPrompt({ responsibilityId: props.responsibilityId });
    }
  } catch (error) {
    console.error('Error bulk activating checkpoints:', error);
    // Close the dialog even on error
    showActivateDialog.value = false;
  } finally {
    // Hide loader when everything completes
    proxy.$loader.setAppLoading(false);
  }
}

async function handleDeleteCheckpoints() {
  if (!props.accountId || !props.responsibilityId || selectedIds.value.length === 0) {
    return;
  }

  proxy.$loader.setAppLoading(true);

  try {
    // Collect checkpoint IDs
    const checkpointIds = selectedIds.value;
    
    // Make API call
    const response = await bulkDeleteCheckpoints(
      props.accountId,
      props.responsibilityId,
      checkpointIds
    );

    // Extract counts from response
    const deletedCount = response?.data?.deleted_count || 0;
    const ignoredCount = response?.data?.ignored_count || 0;

    // Show ConfirmationToast with TIER 4
    const notificationStore = useNotificationStore();
    notificationStore.success(
      'Checkpoints deleted successfully',
      `${deletedCount} deleted, ${ignoredCount} ignored`
    );

    // Refresh checkpoints (this will be handled asynchronously by parent)
    emit('refreshCheckpoints');

    // Clear selection
    emit('update:modelValue', []);

    // Switch to EditCheckpointsTable default view
    emit('switchToDefaultView');

    // Close dialog after everything completes
    showDeleteDialog.value = false;
  } catch (error) {
    console.error('Error bulk deleting checkpoints:', error);
    showDeleteDialog.value = false;
  } finally {
    proxy.$loader.setAppLoading(false);
  }
}

async function handleArchiveCheckpoints() {
  if (!props.accountId || !props.responsibilityId || selectedIds.value.length === 0) {
    return;
  }

  proxy.$loader.setAppLoading(true);

  try {
    const checkpointIds = selectedIds.value;

    const response = await bulkArchiveCheckpoints(
      props.accountId,
      props.responsibilityId,
      checkpointIds
    );

    const archivedCount = response?.data?.archived_count ?? 0;
    const ignoredCount = response?.data?.ignored_count ?? 0;

    const notificationStore = useNotificationStore();
    notificationStore.success(
      'Checkpoints archived successfully',
      `${archivedCount} archived, ${ignoredCount} ignored`
    );

    emit('refreshCheckpoints');
    emit('update:modelValue', []);
    emit('switchToDefaultView');
    showArchiveDialog.value = false;
  } catch (error) {
    console.error('Error bulk archiving checkpoints:', error);
    showArchiveDialog.value = false;
  } finally {
    proxy.$loader.setAppLoading(false);
  }
}

async function handleRetireCheckpoints() {
  if (!props.accountId || !props.responsibilityId || selectedIds.value.length === 0) {
    return;
  }

  proxy.$loader.setAppLoading(true);

  try {
    const checkpointIds = selectedIds.value;

    const response = await bulkRetireCheckpoints(
      props.accountId,
      props.responsibilityId,
      checkpointIds
    );

    const retiredCount = response?.data?.retired_count ?? 0;
    const ignoredCount = response?.data?.ignored_count ?? 0;

    const notificationStore = useNotificationStore();
    notificationStore.success(
      'Checkpoints retired successfully',
      `${retiredCount} retired, ${ignoredCount} ignored`
    );

    emit('refreshCheckpoints');
    emit('update:modelValue', []);
    emit('switchToDefaultView');
    showRetireDialog.value = false;
  } catch (error) {
    console.error('Error bulk retiring checkpoints:', error);
    showRetireDialog.value = false;
  } finally {
    proxy.$loader.setAppLoading(false);
  }
}
</script>

<style scoped>
.ms-bulk-actions-checkpoints-table {
  width: 100%;
}

.ms-bulk-actions-checkpoints-table__toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 0 16px 0;
  width: 100%;
}

.ms-bulk-actions-checkpoints-table__toolbar-left {
  flex: 1 1 auto;
  min-width: 0;
}

.ms-bulk-actions-checkpoints-table__toolbar-right {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding-top: 8px;
}

.ms-bulk-actions-checkpoints-table__switch {
  margin-left: 2px;
}

.ms-bulk-actions-checkpoints-table__table-wrap {
  width: 100%;
  background: #ffffff;
  border-radius: 0;
  overflow: hidden;
}

.ms-bulk-actions-checkpoints-table__table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.ms-bulk-actions-checkpoints-table__th {
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: #667085;
  padding: 14px 16px;
  border-bottom: 1px solid #eaecf0;
  background: #f9fafb;
}

.ms-bulk-actions-checkpoints-table__th--select {
  width: 44px;
  padding-left: 14px;
  padding-right: 8px;
}

.ms-bulk-actions-checkpoints-table__th--status {
  width: 140px;
}

.ms-bulk-actions-checkpoints-table__th--actions {
  width: 64px;
}

.ms-bulk-actions-checkpoints-table__td {
  padding: 16px;
  border-bottom: 1px solid #f2f4f7;
  vertical-align: middle;
  background: #ffffff;
}

.ms-bulk-actions-checkpoints-table__td--select {
  padding-left: 14px;
  padding-right: 8px;
}

.ms-bulk-actions-checkpoints-table__td--status {
  padding-right: 8px;
}

.ms-bulk-actions-checkpoints-table__td--actions {
  padding-right: 16px;
  text-align: right;
}

.ms-bulk-actions-checkpoints-table__row {
  cursor: default;
}

.ms-bulk-actions-checkpoints-table__row:hover .ms-bulk-actions-checkpoints-table__td {
  background: #fcfcfd;
}

.ms-bulk-actions-checkpoints-table__row--compact .ms-bulk-actions-checkpoints-table__td {
  padding-top: 10px;
  padding-bottom: 10px;
}

.ms-bulk-actions-checkpoints-table__checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 1px solid var(--ms-bulk-actions-checkbox-border, #d0d5dd);
  border-radius: 4px;
  background-color: var(--ms-bulk-actions-checkbox-bg, #ffffff);
  background-repeat: no-repeat;
  background-position: center;
  background-size: 12px 12px;
  background-image: var(--ms-bulk-actions-checkbox-unchecked-image, none);
  transition: background-color 120ms ease, border-color 120ms ease;
  position: relative;
  top: 3px;
}

.ms-bulk-actions-checkpoints-table__checkbox:checked {
  border-color: var(--ms-bulk-actions-checkbox-checked-border, #7f56d9);
  background-color: var(--ms-bulk-actions-checkbox-checked-bg, #7f56d9);
  background-image: var(
    --ms-bulk-actions-checkbox-checked-image,
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath d='M2.2 6.2l2.2 2.2L9.8 3' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")
  );
}

.ms-bulk-actions-checkpoints-table__checkbox:indeterminate {
  border-color: var(--ms-bulk-actions-checkbox-indeterminate-border, #7f56d9);
  background-color: var(--ms-bulk-actions-checkbox-indeterminate-bg, #7f56d9);
  background-image: var(
    --ms-bulk-actions-checkbox-indeterminate-image,
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath d='M2 6h8' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")
  );
}

.ms-bulk-actions-checkpoints-table__checkbox:focus-visible {
  outline: 2px solid rgba(127, 86, 217, 0.35);
  outline-offset: 2px;
}

.ms-bulk-actions-checkpoints-table__checkbox--non-interactive {
  pointer-events: none;
}

.ms-bulk-actions-checkpoints-table__status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  border: 1px solid transparent;
  white-space: nowrap;
}

.ms-bulk-actions-checkpoints-table__status-pill--active {
  color: #027a48;
  background: #ecfdf3;
  border-color: #abefc6;
}

.ms-bulk-actions-checkpoints-table__status-pill--draft {
  color: #344054;
  background: #f2f4f7;
  border-color: #eaecf0;
}

.ms-bulk-actions-checkpoints-table__status-pill--retired {
  color: #b54708;
  background: #fffaeb;
  border-color: #fedf89;
}

.ms-bulk-actions-checkpoints-table__status-pill--archived {
  color: #475467;
  background: #f9fafb;
  border-color: #eaecf0;
}

.ms-bulk-actions-checkpoints-table__status-pill--default {
  color: #475467;
  background: #f9fafb;
  border-color: #eaecf0;
}

.ms-bulk-actions-checkpoints-table__chatter {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  cursor: pointer;
}

.ms-bulk-actions-checkpoints-table__chatter:hover {
  background: #f9fafb;
}

.ms-bulk-actions-checkpoints-table__empty {
  padding: 24px 16px;
  text-align: center;
  color: #667085;
  font-size: 14px;
}

/* Grid View Styles */
.ms-bulk-actions-checkpoints-table__grid-wrap {
  width: 100%;
  background: #ffffff;
  border-radius: 8px;
  padding: 16px;
}

.ms-bulk-actions-checkpoints-table__grid-header {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eaecf0;
}

.ms-bulk-actions-checkpoints-table__select-all-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.ms-bulk-actions-checkpoints-table__select-all-text {
  font-size: 14px;
  font-weight: 600;
  color: #344054;
  position: relative;
  top: 3px;
}

.ms-bulk-actions-checkpoints-table__empty-grid {
  padding: 48px 16px;
  text-align: center;
  color: #667085;
  font-size: 14px;
}

.ms-bulk-actions-checkpoints-table__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

.ms-bulk-actions-checkpoints-table__card {
  position: relative;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ms-bulk-actions-checkpoints-table__card:hover {
  border-color: #d0d5dd;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.08);
  transform: translateY(-1px);
}

.ms-bulk-actions-checkpoints-table__card--selected {
  border-color: #7f56d9;
  background: #faf8ff;
}

.ms-bulk-actions-checkpoints-table__card-checkbox-wrapper {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px;
  z-index: 1;
}

.ms-bulk-actions-checkpoints-table__card-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
}

.ms-bulk-actions-checkpoints-table__card-header {
  padding-right: 32px;
  flex: 1;
}

.ms-bulk-actions-checkpoints-table__card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid #f2f4f7;
}

.ms-bulk-actions-checkpoints-table__chatter--card {
  flex-shrink: 0;
}

/* Responsive Grid */
@media (max-width: 1200px) {
  .ms-bulk-actions-checkpoints-table__grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
}

@media (max-width: 768px) {
  .ms-bulk-actions-checkpoints-table__grid {
    grid-template-columns: 1fr;
  }
}
</style>

